import React, { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { Area, Size } from '../../../../../shared/helpers/layout-manager/types';
import { useResizeObserver } from '../../../../../shared/hooks';
import { setVideoContainerSize } from '../slice';

const INITIAL_LAYOUT_SIZE: Size = { width: 0, height: 0 };

const transformToStyle = (areas: Area[]): React.CSSProperties[] => {
    return areas.map((area) => {
        return {
            transform: `translate(${area.x}px, ${area.y}px)`,
            width: area.width,
            height: area.height,
        };
    });
};

type GetAreaCallback = (layoutSize: Size, totalCount: number) => Area[];
type GetStartButtonStyles = (areas: Area[], isNewGrid?: boolean) => React.CSSProperties;
type GetEndButtonStyles = (areas: Area[], containerWidth: number, isNewGrid?: boolean) => React.CSSProperties;

export interface PositionHelpers {
    getAreas: GetAreaCallback;
    getStartButtonStyles: GetStartButtonStyles;
    getEndButtonStyles: GetEndButtonStyles;
}

interface HookParams {
    syntheticEndNodeCount: number;
    syntheticStartNodeCount: number;
    handleResize(elem?: Element): void;
}

export const useCommonLayoutStyles = (params: HookParams, helpers: PositionHelpers) => {
    const { syntheticEndNodeCount, syntheticStartNodeCount, handleResize } = params;

    const { getAreas, getStartButtonStyles, getEndButtonStyles } = helpers;

    const [layoutSize, setLayoutSize] = React.useState<Size>(INITIAL_LAYOUT_SIZE);

    const dispatch = useDispatch();

    const onResize = React.useCallback(
        (elem?: Element) => {
            if (!elem) {
                return;
            }

            dispatch(setVideoContainerSize({ width: elem.clientWidth, height: elem.clientHeight }));
            setLayoutSize({ width: elem.clientWidth, height: elem.clientHeight });

            handleResize(elem);
        },
        [handleResize, dispatch]
    );

    const resize = useResizeObserver<HTMLDivElement>({ callback: onResize });

    React.useLayoutEffect(() => {
        const elem = resize.ref.current;
        if (elem) {
            dispatch(setVideoContainerSize({ width: elem.clientWidth, height: elem.clientHeight }));
            setLayoutSize({ width: elem.clientWidth, height: elem.clientHeight });
        }
    }, [resize.ref, dispatch]);

    const [gridLayoutStyles, setGridLayoutStyles] = React.useState<React.CSSProperties[]>([]);
    const [startButtonStyles, setStartButtonStyles] = React.useState<React.CSSProperties>({});
    const [endButtonStyles, setEndButtonStyles] = React.useState<React.CSSProperties>({});

    const getLayout = React.useCallback(
        (count: number) => {
            // Считаем общее количество слотов, добавляя наши касмомные
            const totalCount = count + syntheticEndNodeCount + syntheticStartNodeCount;

            // Получаем координаты для слотов
            const areas = getAreas(layoutSize, totalCount);
            const layout = transformToStyle(areas);

            const containerWidth = resize.ref.current?.clientWidth || window.innerWidth;

            // Сохраняем стили всех элементов в сетке, включая кастомные слоты, для использования в компоненте
            setGridLayoutStyles(layout);
            setStartButtonStyles(getStartButtonStyles(areas));
            setEndButtonStyles(getEndButtonStyles(areas, containerWidth));

            // Но в голум отдаем только координаты слотов, которые не заняты нашими кастомными компонентами
            const goloomAreas = areas.slice(syntheticStartNodeCount, areas.length - syntheticEndNodeCount);
            const goloomLayout = transformToStyle(goloomAreas);
            return goloomLayout;
        },
        [
            syntheticEndNodeCount,
            syntheticStartNodeCount,
            layoutSize,
            getAreas,
            getStartButtonStyles,
            getEndButtonStyles,
            resize.ref,
        ]
    );

    const getSlotsSpec = useCallback(
        (slotsCount) => {
            return getAreas(layoutSize, slotsCount).map(({ width, height }) => ({ width, height }));
        },
        [getAreas, layoutSize]
    );

    return {
        getLayout,
        gridLayoutStyles,
        resize,
        startButtonStyles,
        endButtonStyles,
        getSlotsSpec,
    };
};
